/**
 * RTWO Asset & Token Engine — Service Factory
 */

import { assInternalEventBus } from '../events/ass-event-bus';
import {
  cryptographyAdapterRegistry,
  eventBusAdapterRegistry,
  ledgerAdapterRegistry,
  scrtRuntimeAdapterRegistry,
  storageAdapterRegistry,
  vmRuntimeAdapterRegistry,
  type CryptographyAdapterRegistry,
  type EventBusAdapterRegistry,
  type LedgerAdapterRegistry,
  type ScrtRuntimeAdapterRegistry,
  type StorageAdapterRegistry,
  type VmRuntimeAdapterRegistry,
} from '../registry/adapter-registries';
import { createASSRepositories, type ASSRepositories } from '../repositories/ass-repositories';
import { AssetEventPublisherService } from './asset-event-publisher-service';
import { AssetReadinessService } from './asset-readiness-service';
import { AssetRegistryService } from './asset-registry-service';
import { AssetValidationService } from './asset-validation-service';
import { MetadataService } from './metadata-service';
import { PermissionService } from './permission-service';
import { SupplyPolicyService } from './supply-policy-service';
import { TokenLifecycleService } from './token-lifecycle-service';
import { TokenStandardRegistryService } from './token-standard-registry-service';
import { TransferRuleService } from './transfer-rule-service';
import type { ASSServiceContext } from './base-ass-service';

export interface ASSEngineServices {
  assetRegistry: AssetRegistryService;
  tokenStandardRegistry: TokenStandardRegistryService;
  supplyPolicyService: SupplyPolicyService;
  permissionService: PermissionService;
  transferRuleService: TransferRuleService;
  metadataService: MetadataService;
  assetEventPublisher: AssetEventPublisherService;
  assetValidationService: AssetValidationService;
  assetReadinessService: AssetReadinessService;
  tokenLifecycleService: TokenLifecycleService;
}

export function createASSEngineServices(options?: {
  repositories?: ASSRepositories;
  ledgerRegistry?: LedgerAdapterRegistry;
  storageRegistry?: StorageAdapterRegistry;
  scrtRegistry?: ScrtRuntimeAdapterRegistry;
  vmRegistry?: VmRuntimeAdapterRegistry;
  cryptographyRegistry?: CryptographyAdapterRegistry;
  eventBusRegistry?: EventBusAdapterRegistry;
  eventBus?: typeof assInternalEventBus;
}): {
  repositories: ASSRepositories;
  ledgerRegistry: LedgerAdapterRegistry;
  storageRegistry: StorageAdapterRegistry;
  scrtRegistry: ScrtRuntimeAdapterRegistry;
  vmRegistry: VmRuntimeAdapterRegistry;
  cryptographyRegistry: CryptographyAdapterRegistry;
  eventBusRegistry: EventBusAdapterRegistry;
  services: ASSEngineServices;
  context: ASSServiceContext;
} {
  const repositories = options?.repositories ?? createASSRepositories();
  const ledgerRegistry = options?.ledgerRegistry ?? ledgerAdapterRegistry;
  const storageRegistry = options?.storageRegistry ?? storageAdapterRegistry;
  const scrtRegistry = options?.scrtRegistry ?? scrtRuntimeAdapterRegistry;
  const vmRegistry = options?.vmRegistry ?? vmRuntimeAdapterRegistry;
  const cryptographyRegistry = options?.cryptographyRegistry ?? cryptographyAdapterRegistry;
  const eventBusRegistry = options?.eventBusRegistry ?? eventBusAdapterRegistry;
  const context: ASSServiceContext = {
    eventBus: options?.eventBus ?? assInternalEventBus,
  };

  const services: ASSEngineServices = {
    assetRegistry: new AssetRegistryService(context, repositories, ledgerRegistry),
    tokenStandardRegistry: new TokenStandardRegistryService(context),
    supplyPolicyService: new SupplyPolicyService(
      context,
      repositories,
      ledgerRegistry,
      scrtRegistry
    ),
    permissionService: new PermissionService(context, repositories, cryptographyRegistry),
    transferRuleService: new TransferRuleService(
      context,
      repositories,
      ledgerRegistry,
      scrtRegistry
    ),
    metadataService: new MetadataService(context, repositories, storageRegistry),
    assetEventPublisher: new AssetEventPublisherService(context, repositories, eventBusRegistry),
    assetValidationService: new AssetValidationService(context, ledgerRegistry, scrtRegistry),
    assetReadinessService: new AssetReadinessService(context),
    tokenLifecycleService: new TokenLifecycleService(
      context,
      repositories,
      ledgerRegistry,
      scrtRegistry,
      vmRegistry
    ),
  };

  return {
    repositories,
    ledgerRegistry,
    storageRegistry,
    scrtRegistry,
    vmRegistry,
    cryptographyRegistry,
    eventBusRegistry,
    services,
    context,
  };
}
